import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { withPageAuthRequired } from "@common/middleware";
import MutateTaskModal from "@modules/tasks/components/MutateTaskModal";
import RemoveTaskModal from "@modules/tasks/components/RemoveTaskModal";
import { HiOutlineSupport, HiPlus } from "react-icons/hi";
import { TaskTabSelected } from "@modules/tasks/constants";
import LoadingIndicator from "@common/components/LoadingIndicator";
import useTasksStore from "@modules/tasks/state";
import {
  usePizzaTrackerStateMachine,
  useTasksList,
} from "@modules/tasks/hooks";
import TasksGrid from "@modules/tasks/components/TasksGrid";
import { useUser } from "@auth0/nextjs-auth0/client";
import { useHousehold } from "@common/queries";
import PizzaTrackerContainer from "@modules/tasks/components/PizzaTrackerContainer";
import { useDocusignToastMessages } from "@modules/docusign/hooks";
import {
  useCreateTask,
  useDeleteTask,
  useEditTask,
} from "@modules/tasks/mutations";
import IntakeFormModal from "@modules/tasks/components/IntakeFormModal";
import { ProductPhase, TaskTemplateId, WizardId } from "@modules/tasks/types";
import { Button, Typography } from "@earned/core";
import { DisabilityInsuranceWizardController } from "@modules/tasks/components/DisabilityInsuranceWizardController";
import { TermLifeInsuranceWizardController } from "@modules/tasks/components/TermLifeInsuranceWizardController";
import { ProfileWizardController } from "@modules/tasks/components/ProfileWizardController";
import { RiskToleranceWizardController } from "@modules/tasks/components/RiskToleranceWizardController";
import { themeTextColors, ZeroStateModules } from "@common/constants";
import TopNavbar from "@common/components/TopNavbar";
import { useIsCobranded, useMGPWizardEnabled } from "@common/hooks";
import ZeroState from "@modules/zerostate/components/ZeroState";
import { MGPWizardController } from "@modules/mgp/components/MGPWizardController";
import { ADVISOR_SUPPORT_EMAIL } from "@modules/advisory-team/constants";

const TasksAndActionsPage: FC = () => {
  const {
    selectedTask,
    setSelectedTask,
    removeTaskModalVisible,
    setRemoveTaskModalVisible,
    addNewTaskModalVisible,
    setAddNewTaskModalVisible,
    newSelectedTab,
    setNewSelectedTab,
    setIntakeFormModalVisible,
    intakeFormModalVisible,
    wizardId,
  } = useTasksStore();

  const {
    openTasks: openTasksBase,
    completedTasks,
    isLoading,
    refetchTaskList,
    refetchTasksOpenCount,
  } = useTasksList();

  const enabled = useMGPWizardEnabled();
  const openTasks = useMemo(
    () =>
      openTasksBase.filter(
        (task) =>
          (task.templateId === TaskTemplateId.MGP_QUESTIONNAIRE_WIZARD &&
            enabled) ||
          task.templateId !== TaskTemplateId.MGP_QUESTIONNAIRE_WIZARD
      ),
    [enabled, openTasksBase]
  );

  const { mutateAsync: createTask } = useCreateTask();
  const { mutateAsync: editTask } = useEditTask();
  const { mutateAsync: deleteTask } = useDeleteTask();

  const addNewTask = () => {
    setSelectedTask(null);
    setAddNewTaskModalVisible(true);
  };

  const confirmRemoveTask = useCallback(async () => {
    setRemoveTaskModalVisible(false);

    if (selectedTask) {
      await deleteTask({
        id: selectedTask.id,
      });
      await refetchTaskList();
      await refetchTasksOpenCount();
    }
  }, [
    deleteTask,
    refetchTaskList,
    refetchTasksOpenCount,
    selectedTask,
    setRemoveTaskModalVisible,
  ]);

  const { user } = useUser();

  const isAdvisor = useMemo(
    () =>
      user && user["https://formefinancial.com/advisor"] === true
        ? true
        : false,
    [user]
  );

  const { data: household, isLoading: isLoadingHousehold } = useHousehold();
  const { changeProductCurrentPhase } = usePizzaTrackerStateMachine();

  const [selectedPhase, setSelectedPhase] = useState<
    ProductPhase | undefined
  >();
  const currentPhase: ProductPhase = useMemo(
    () =>
      selectedPhase
        ? selectedPhase
        : (household?.currentProduct?.currentPhase as ProductPhase),
    [household, selectedPhase]
  );

  const onClickPizzaTracker = useCallback(
    (phase: ProductPhase) => {
      setSelectedPhase(phase);
    },
    [setSelectedPhase]
  );

  useEffect(() => {
    if (!isLoading) {
      changeProductCurrentPhase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, completedTasks, openTasks]);

  useDocusignToastMessages();

  const { isCobranded, isCobrandedClient } = useIsCobranded();

  if (isCobranded && !isCobrandedClient) {
    return (
      <div className="relative flex flex-1 flex-col">
        <ZeroState name={ZeroStateModules.CobrandedExperience} />
      </div>
    );
  }

  if (isLoadingHousehold || !household)
    return <LoadingIndicator isVisible={true} />;

  return (
    <>
      {wizardId === WizardId.DISABILITY_INSURANCE && (
        <DisabilityInsuranceWizardController clients={household.clients} />
      )}
      {wizardId === WizardId.TERM_LIFE_INSURANCE && (
        <TermLifeInsuranceWizardController clients={household.clients} />
      )}
      {wizardId === WizardId.RISK_TOLERANCE && (
        <RiskToleranceWizardController clients={household.clients} />
      )}
      {wizardId === WizardId.PROFILE && (
        <ProfileWizardController clients={household.clients} />
      )}
      {wizardId === WizardId.MONEY_GUIDE_PRO && <MGPWizardController />}
      <RemoveTaskModal
        modalType={removeTaskModalVisible ? "single" : null}
        onClose={() => setRemoveTaskModalVisible(false)}
        onConfirm={() => {
          confirmRemoveTask();
          setAddNewTaskModalVisible(false);
        }}
      />
      <MutateTaskModal
        defaultPhase={currentPhase as ProductPhase}
        isOpen={addNewTaskModalVisible}
        onClose={() => setAddNewTaskModalVisible(false)}
        onDeleteTask={() => {
          if (selectedTask?.id) setRemoveTaskModalVisible(true);
        }}
        onConfirm={(task: {
          name: string;
          description?: string;
          taskId?: string;
          productId?: string;
          estimatedCompletionTime?: number;
          callToAction?: string;
          productTopic?: string;
          productPhase?: string;
          templateId?: string;
          downloadablePdfUrl?: string;
          ctaUrl?: string;
          ctaText?: string;
        }) =>
          !!task.taskId
            ? editTask({
                id: task.taskId,
                ...task,
              })
            : createTask({
                ...task,
              })
        }
        editParams={selectedTask}
        isEdit={!!selectedTask}
        household={household}
      />
      <IntakeFormModal
        isOpen={intakeFormModalVisible}
        onClose={() => setIntakeFormModalVisible(false)}
      />
      <div className="flex w-full flex-1 flex-col gap-4 overflow-y-auto bg-gray-50 scrollbar-none">
        {household?.currentProduct && currentPhase ? (
          <PizzaTrackerContainer
            onClick={onClickPizzaTracker}
            product={household.currentProduct.templateId}
            openTasks={openTasks}
            completedTasks={completedTasks}
            currentPhase={currentPhase}
            ongoingWealthManagement={
              household.salesforceAccountOngoingWealthManagement
            }
          />
        ) : (
          <>
            {isAdvisor && (
              <TopNavbar title="Tasks & Actions">
                <button
                  className="text-heading-6 font-medium text-white md:mt-1"
                  onClick={addNewTask}
                >
                  <HiPlus color="black" />
                </button>
              </TopNavbar>
            )}
          </>
        )}
        <div className="flex flex-col gap-4 px-8 py-2 lg:px-10">
          <div className="flex max-w-[1060px] flex-row justify-between">
            <div className="flex flex-row gap-4">
              <button
                onClick={() => {
                  refetchTaskList();
                  setNewSelectedTab(TaskTabSelected.TASKS);
                }}
              >
                <Typography
                  variant="body-sm"
                  weight={
                    newSelectedTab === TaskTabSelected.TASKS
                      ? "medium"
                      : "normal"
                  }
                  color={
                    newSelectedTab === TaskTabSelected.TASKS
                      ? "primary"
                      : "secondary"
                  }
                  className={
                    newSelectedTab === TaskTabSelected.TASKS
                      ? ""
                      : "tracking-wide"
                  }
                >
                  Today’s Tasks
                </Typography>
              </button>
              <button
                onClick={() => {
                  refetchTaskList();
                  setNewSelectedTab(TaskTabSelected.ACHIEVEMENTS);
                }}
              >
                <Typography
                  variant="body-sm"
                  weight={
                    newSelectedTab === TaskTabSelected.ACHIEVEMENTS
                      ? "medium"
                      : "normal"
                  }
                  color={
                    newSelectedTab === TaskTabSelected.ACHIEVEMENTS
                      ? "primary"
                      : "secondary"
                  }
                  className={
                    newSelectedTab === TaskTabSelected.ACHIEVEMENTS
                      ? ""
                      : "tracking-wide"
                  }
                >
                  Tasks Completed
                </Typography>
              </button>
            </div>
            <div className="flex flex-none flex-row items-center">
              <Button
                variant="tertiary"
                size="sm"
                href={`mailto:${ADVISOR_SUPPORT_EMAIL}`}
              >
                <div className="flex flex-row items-center gap-1">
                  <HiOutlineSupport
                    color={themeTextColors.secondary}
                    size={24}
                  />
                  <Typography variant="body-sm" weight="medium">
                    Need help?
                  </Typography>
                </div>
              </Button>
              {household?.currentProduct && isAdvisor && (
                <Button variant="primary" size="sm" onClick={addNewTask}>
                  Create task
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="-mb-[72px] flex flex-1 pb-8 scrollbar-none lg:mb-0 lg:flex-row lg:pb-0">
          <div className="flex w-full flex-col">
            <div className="flex w-full flex-1 flex-col px-8 lg:px-10">
              <LoadingIndicator isVisible={isLoading} />
              {!isLoading && (
                <>
                  {newSelectedTab === TaskTabSelected.TASKS ? (
                    <TasksGrid
                      isCompleted={false}
                      tasks={openTasks}
                      product={household?.currentProduct?.templateId}
                      isAdvisor={isAdvisor}
                    />
                  ) : (
                    <TasksGrid
                      isCompleted={true}
                      tasks={completedTasks}
                      isAdvisor={isAdvisor}
                      product={household?.currentProduct?.templateId}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withPageAuthRequired(TasksAndActionsPage);
